import type { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Button } from "./Button.js";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** Custom footer; pass `null` to hide; omit for default OK/Cancel buttons. */
  footer?: ReactNode | null;
  okText?: string;
  cancelText?: string;
  onOk?: () => void;
  confirmLoading?: boolean;
  width?: number;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  okText,
  cancelText,
  onOk,
  confirmLoading,
  width = 520,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[1100] bg-black/50 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          style={{ width, maxWidth: "calc(100vw - 2rem)" }}
          className="fixed top-1/2 left-1/2 z-[1100] grid max-h-[calc(100vh-4rem)] -translate-x-1/2 -translate-y-1/2 grid-rows-[auto_1fr_auto] overflow-hidden rounded-xl border bg-card text-card-foreground shadow-lg data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <div className="flex items-center justify-between gap-4 border-b px-6 py-4">
            {title != null ? (
              <Dialog.Title className="text-base font-semibold">{title}</Dialog.Title>
            ) : (
              <Dialog.Title className="sr-only">Dialog</Dialog.Title>
            )}
            <Dialog.Close className="rounded-sm text-muted-foreground opacity-70 transition hover:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
              <X className="size-4" />
              <span className="sr-only">Kapat</span>
            </Dialog.Close>
          </div>
          <div className="overflow-y-auto px-6 py-4">{children}</div>
          {footer !== null && (
            <div className="flex justify-end gap-2 border-t px-6 py-4">
              {footer === undefined ? (
                <>
                  <Button variant="default" onClick={onClose}>
                    {cancelText ?? "İptal"}
                  </Button>
                  <Button variant="primary" loading={confirmLoading} onClick={onOk}>
                    {okText ?? "Tamam"}
                  </Button>
                </>
              ) : (
                footer
              )}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
